import React, { useState, useEffect } from "react";
import axios from "axios";

import { Container, Row, Col, Form, Button } from "react-bootstrap";
import { Link, useNavigate } from "react-router-dom";

import { toast } from "react-toastify";
import { ROLE_BASED_API } from "../../api/api";
import { FaEye, FaEyeSlash } from "react-icons/fa";
import { IoArrowBack } from "react-icons/io5";

import "./ForgotPassword.css";

import forgotImg from "../../assets/loginpage-image.png";
import logo from "../../assets/Skilledition-logo.png";

const ForgotPassword = () => {
  const navigate = useNavigate();

  const [forgotData, setForgotData] = useState({
    email: "",
    otp: "",
    newPassword: "",
    confirmPassword: "",
  });

  const [emailOtpId, setEmailOtpId] = useState(null);
  const [step, setStep] = useState(1); // 1 = email/OTP, 2 = reset password

  const [showPassword, setShowPassword] = useState({
    new: false,
    confirm: false,
  });

  const [otpSent, setOtpSent] = useState(false);
  const [otpExpired, setOtpExpired] = useState(false);
  const [otpTimer, setOtpTimer] = useState(0);

  // loading
  const [otpLoading, setOtpLoading] = useState(false);
  const [verifyLoading, setVerifyLoading] = useState(false);
  const [loadingForgPassword, setForgPasswordLoading] = useState(false);

  // main toast error
  const showErrorToast = (message) => {
    toast.error(message, {
      position: "top-right",
      autoClose: 1500,
      theme: "colored",
    });
  };

  //  Reset when email changes
  useEffect(() => {
    setOtpSent(false);
    setOtpExpired(false);
    setOtpTimer(0);
    setEmailOtpId(null);
    setStep(1);
  }, [forgotData.email]);

  // Countdown timer
  useEffect(() => {
    if (otpTimer <= 0) {
      if (otpSent) {
        setOtpExpired(true);
        setOtpSent(false);
      }
      return;
    }

    const interval = setInterval(() => {
      setOtpTimer((prev) => prev - 1);
    }, 1000);

    return () => clearInterval(interval);
  }, [otpTimer, otpSent]);

  // const formatTime = (seconds) => {
  //   const m = Math.floor(seconds / 60);
  //   const s = seconds % 60;
  //   return `${m}:${s < 10 ? "0" : ""}${s}`;
  // };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForgotData((prev) => ({ ...prev, [name]: value }));
  };

  const togglePassword = (field) => {
    setShowPassword((prev) => ({
      ...prev,
      [field]: !prev[field],
    }));
  };

  const handleSendOtp = async () => {
    if (otpLoading) return;

    if (!forgotData.email) {
      showErrorToast("Please enter email first");
      return;
    }

    setOtpLoading(true);
    try {
      const response = await axios.post(ROLE_BASED_API.FORGOT_PASSWORD_OTP, {
        email: forgotData.email,
      });

      const data = response.data;

      if (data.statusCode === 200) {
        toast.success(data.message, {
          position: "top-right",
          autoClose: 1500,
          theme: "colored",
        });

        setEmailOtpId(data.email_otp_id);

        setOtpSent(true);
        setOtpExpired(false);
        setOtpTimer(300);
      } else {
        // This will handle any 2xx response that is not 201
        showErrorToast(data.message);
      }
    } catch (error) {
      console.error("Otp Error:", error);
      // Server responded with non-2xx status (like 400, 500)

      if (error.response?.data?.message) {
        showErrorToast(error.response.data.message);
      } else {
        showErrorToast("Network error! Please try again.");
      }
    } finally {
      setOtpLoading(false);
    }
  };

  const handleVerifyOtp = async (e) => {
    e.preventDefault();

    if (!/^\d{6}$/.test(forgotData.otp)) {
      showErrorToast("OTP must be 6 digits");
      return;
    }

    setVerifyLoading(true);
    try {
      const response = await axios.post(ROLE_BASED_API.VERIFY_OTP, {
        email_otp_id: emailOtpId,
        otp: forgotData.otp,
      });

      const data = response.data;

      if (data.statusCode === 200) {
        toast.success(data.message, {
          position: "top-right",
          autoClose: 1500,
          theme: "colored",
        });

        setStep(2); // Move to password reset
        setOtpSent(false);
        setOtpExpired(false);
      } else {
        // This will handle any 2xx response that is not 201
        showErrorToast(data.message);
      }
    } catch (error) {
      console.error("Otp Verifiying Error:", error);
      // Server responded with non-2xx status (like 400, 500)

      if (error.response?.data?.message) {
        showErrorToast(error.response.data.message);
      } else {
        showErrorToast("Network error! Please try again.");
      }
    } finally {
      setVerifyLoading(false);
    }
  };

  const handlePasswordReset = async (e) => {
    e.preventDefault();

    if (forgotData.newPassword !== forgotData.confirmPassword) {
      showErrorToast("New password and confirm password do not match!");
      return;
    }

    const payload = {
      email_otp_id: emailOtpId,
      new_password: forgotData.newPassword,
      confirm_password: forgotData.confirmPassword,
    };

    setForgPasswordLoading(true);
    try {
      const response = await axios.post(
        ROLE_BASED_API.FORGOT_PASSWORD,
        payload,
      );

      const data = response.data;

      if (data.statusCode === 200) {
        toast.success(data.message, {
          position: "top-right",
          autoClose: 1500,
          theme: "colored",
        });

        setTimeout(() => {
          navigate("/signin");
        }, 1500);
      } else {
        // This will handle any 2xx response that is not 201
        showErrorToast(data.message);
      }
    } catch (error) {
      console.error("ForgotPassword Error:", error);
      // Server responded with non-2xx status (like 409)
      if (error.response?.data?.message) {
        showErrorToast(error.response.data.message);
      } else {
        // Network error or server didn't respond
        showErrorToast("Network error! Please try again.");
      }
    } finally {
      setForgPasswordLoading(false);
    }
  };

  return (
    <section className="student-forgot-section">
      <Container className="student-forgot-container">
        <Row className="student-forgot-row">
          {/* Left Side - Form */}
          <Col xs={12} md={6} className="student-forgot-left">
            <div className="student-forgot-content">
              <div className="student-forgot-logo mb-4">
                <img src={logo} alt="Skilledition Logo" />
              </div>

              <div className="student-forgot-backmoving-button-wrapper">
                <button
                  className="student-forgot-backmoving-button-main"
                  onClick={() => navigate(-1)}
                >
                  <IoArrowBack className="student-forgot-backmoving-button-icon" />
                  <span className="student-forgot-backmoving-button-text">
                    Back
                  </span>
                </button>
              </div>

              {step === 1 ? (
                <>
                  <h5 className="student-forgot-title">Forgot Password</h5>
                  <p className="student-forgot-subtitle mb-3">
                    Enter your email address to receive a password reset OTP 
                  </p>

                  <Form
                    onSubmit={handleVerifyOtp}
                    className="student-forgot-form"
                  >
                    <Form.Group className="student-forgot-form-group">
                      <Form.Label className="student-forgot-label">
                        Enter Email
                      </Form.Label>

                      <Form.Control
                        type="email"
                        name="email"
                        placeholder="Enter your email"
                        className="student-forgot-input"
                        value={forgotData.email}
                        onChange={handleChange}
                        required
                        pattern="[a-z0-9._%+\-]+@[a-z0-9.\-]+\.[a-z]{2,}"
                        title="Please enter a valid email address."
                      />

                      <span
                        onClick={
                          !otpLoading && (!otpSent || otpExpired)
                            ? handleSendOtp
                            : undefined
                        }
                        className="student-signup-get-otp-text"
                      >
                        {otpLoading
                          ? "Sending..."
                          : otpExpired
                            ? "Resend OTP"
                            : otpSent
                              ? "OTP sent successfully"
                              : "Get OTP"}
                      </span>
                    </Form.Group>

                    {otpSent && (
                      <Form.Group
                        controlId="otp"
                        className="student-forgot-form-group"
                      >
                        <Form.Label className="student-forgot-label">
                          Enter Otp
                        </Form.Label>
                        <Form.Control
                          type="text"
                          name="otp"
                          placeholder="Enter OTP"
                          className="student-signup-input"
                          value={forgotData.otp}
                          onChange={handleChange}
                          maxLength={6}
                          required
                        />
                      </Form.Group>
                    )}

                    <Button
                      type="submit"
                      className="student-forgot-btn"
                      disabled={verifyLoading || !otpSent || otpExpired}
                    >
                      {verifyLoading ? "Verifying..." : "Verify OTP"}
                    </Button>
                  </Form>

                  <div className="student-forgot-back mt-3">
                    <p>
                      Have your password?{" "}
                      <Link to="/signin" className="student-forgot-back-link">
                        Sign In
                      </Link>
                    </p>
                  </div>
                </>
              ) : (
                <>
                  <h5 className="student-forgot-title">Reset Password</h5>
                  <p className="student-forgot-subtitle mb-3">
                    Enter your new password below.
                  </p>

                  <Form onSubmit={handlePasswordReset}>
                    <Form.Group className="student-forgot-form-group">
                      <Form.Label className="student-forgot-label">
                        New Password
                      </Form.Label>

                      <div className="student-signin-password-input-wrapper">
                        <Form.Control
                          type={showPassword.new ? "text" : "password"}
                          name="newPassword"
                          placeholder="Enter new password"
                          className="student-forgot-input"
                          value={forgotData.newPassword}
                          onChange={handleChange}
                          required
                          pattern="(?=.*\d)(?=.*[a-z])(?=.*[A-Z])(?=.*[!@#$%^&*]).{8,}"
                          title="Min 8 chars with uppercase, lowercase, number & symbol"
                        />
                        <span
                          className="student-signin-password-toggle-icon"
                          onClick={() => togglePassword("new")}
                        >
                          {showPassword.new ? <FaEyeSlash /> : <FaEye />}
                        </span>
                      </div>
                    </Form.Group>

                    <Form.Group className="student-forgot-form-group mt-3">
                      <Form.Label className="student-forgot-label">
                        Confirm Password
                      </Form.Label>

                      <div className="student-signin-password-input-wrapper">
                        <Form.Control
                          type={showPassword.confirm ? "text" : "password"}
                          name="confirmPassword"
                          placeholder="Confirm new password"
                          className="student-forgot-input"
                          value={forgotData.confirmPassword}
                          onChange={handleChange}
                          required
                          pattern="(?=.*\d)(?=.*[a-z])(?=.*[A-Z])(?=.*[!@#$%^&*]).{8,}"
                          title="Min 8 chars with uppercase, lowercase, number & symbol"
                        />
                        <span
                          className="student-signin-password-toggle-icon"
                          onClick={() => togglePassword("confirm")}
                        >
                          {showPassword.confirm ? <FaEyeSlash /> : <FaEye />}
                        </span>
                      </div>
                    </Form.Group>

                    <Button
                      type="submit"
                      className="student-forgot-btn mt-4"
                      disabled={loadingForgPassword}
                    >
                      {loadingForgPassword ? "Reseting..." : "Reset Password"}
                    </Button>
                  </Form>
                </>
              )}
            </div>
          </Col>

          {/* Right Side */}
          <Col xs={12} md={6} className="student-forgot-right d-none d-md-flex">
            <div className="student-forgot-right-content">
              <h2 className="student-forgot-right-title">
                Reset your password
              </h2>
              <p className="student-forgot-right-desc">
                Recover your account securely and continue your learning journey. 
              </p>
              <img
                src={forgotImg}
                alt="Forgot password visual"
                className="student-forgot-image"
              />
            </div>
          </Col>
        </Row>
      </Container>
    </section>
  );
};

export default ForgotPassword;
